import { z } from "zod";

// Apart from the rest of the schemas, whose messages bring the whole of the identity copy: the
// sign-in routes validate their search with these, and a route's search rides in every first load.

/** 254 is the longest address RFC 5321 will carry, and the column has no narrower limit. */
export const EMAIL_MAX = 254;

export const socialProvider = z.enum(["google"]);
export type SocialProvider = z.infer<typeof socialProvider>;

// Better Auth's reset callback sets `?token=…` or `?error=INVALID_TOKEN`. They only pick what to
// render; the server that issued the token checks it.
export const resetSearch = z.object({
  token: z.string().max(256).optional().catch(undefined),
  error: z.string().max(64).optional().catch(undefined),
});

// Only a path on this app: a browser follows `//host` and `/\host` to another host, and the URL
// parser strips a tab, CR or LF between the slashes before reading the host. Anything else falls
// back to `/` rather than refusing the sign-in.
export const signInSearch = z.object({
  redirect: z
    .string()
    .max(2048)
    .refine((value) => /^\/(?![\t\n\r]*[/\\])/.test(value))
    .catch("/")
    // `.catch` alone still demands the key, so every link would have to spell out a destination.
    .default("/"),
  /** A starting value only, e.g. from an invitation: the person can change it. */
  email: z.email().max(EMAIL_MAX).optional().catch(undefined),
  /** Set by the Worker's error callback when a social sign-in did not complete. */
  problem: socialProvider.optional().catch(undefined),
  /** Better Auth's reason beside `problem`; only `account_not_linked` has a screen of its own. */
  error: z.string().max(64).optional().catch(undefined),
});

/** A destination the Worker answers, such as an emailed download: a page load, not a route. */
export const servedByWorker = (href: string) => href.startsWith("/api/");

export const signUpSearch = signInSearch.extend({
  /** Set by sign-in when the address is not confirmed yet and a code is on its way. */
  verify: z.boolean().optional().catch(undefined),
});
